"use client";

import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBell,
  faSpinner,
  faStar,
  faBug,
  faWrench,
  faBullhorn,
  faExclamationTriangle,
  faCalendarAlt,
  faExternalLinkAlt,
} from "@fortawesome/free-solid-svg-icons";
import { supabase } from "@/utils/supabase/client";
import Link from "next/link";
import Image from "next/image";
import useStore from "@/store/useStore";

// Enhanced tag icon and color mapping
const tagIcons = {
  feature: faStar,
  improvement: faWrench,
  new_widget: faBell,
  bug_fix: faBug,
  announcement: faBullhorn,
  breaking_change: faExclamationTriangle,
  security: faStar, // You can import more icons as needed
  performance: faStar,
  ui_update: faStar,
  integration: faStar,
  api_change: faStar,
  mobile: faStar,
  desktop: faStar,
  accessibility: faStar,
  beta: faStar,
  experimental: faStar,
  deprecation: faStar,
  maintenance: faStar,
};

const tagColors = {
  feature: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  improvement: "bg-green-500/20 text-green-400 border-green-500/30",
  new_widget: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  bug_fix: "bg-red-500/20 text-red-400 border-red-500/30",
  announcement: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  breaking_change: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  security: "bg-red-500/20 text-red-400 border-red-500/30",
  performance: "bg-green-500/20 text-green-400 border-green-500/30",
  ui_update: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  integration: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  api_change: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  mobile: "bg-teal-500/20 text-teal-400 border-teal-500/30",
  desktop: "bg-indigo-500/20 text-indigo-400 border-indigo-500/30",
  accessibility: "bg-green-500/20 text-green-400 border-green-500/30",
  beta: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  experimental: "bg-pink-500/20 text-pink-400 border-pink-500/30",
  deprecation: "bg-gray-500/20 text-gray-400 border-gray-500/30",
  maintenance: "bg-gray-500/20 text-gray-400 border-gray-500/30",
};

// Rich content section renderer
function RichContentSection({ section }) {
  switch (section.section_type) {
    case "content":
      return (
        <div className="prose prose-invert max-w-none">
          {section.title && (
            <h3 className="text-lg font-semibold text-neutral-light mb-3">
              {section.title}
            </h3>
          )}
          <div
            className="text-neutral-light/80"
            dangerouslySetInnerHTML={{ __html: section.content }}
          />
        </div>
      );

    case "image":
      return (
        <div className="my-6">
          {section.metadata?.url && (
            <div className="rounded-lg overflow-hidden">
              <img
                src={section.metadata.url}
                alt={section.metadata.alt || ""}
                className="w-full h-auto"
              />
              {section.metadata.caption && (
                <p className="text-sm text-neutral-light/60 mt-2 text-center">
                  {section.metadata.caption}
                </p>
              )}
            </div>
          )}
        </div>
      );

    case "link":
      return (
        <div className="my-4">
          <a
            href={section.metadata?.url}
            target={section.metadata?.target || "_blank"}
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-accent-coral/20 text-accent-coral hover:bg-accent-coral/30 rounded-lg transition-colors"
          >
            {section.content}
            <FontAwesomeIcon icon={faExternalLinkAlt} className="w-3 h-3" />
          </a>
        </div>
      );

    case "divider":
      return (
        <div className="my-8">
          <hr className="border-dark-elevated" />
        </div>
      );

    default:
      return null;
  }
}

export default function ChangelogPage() {
  const { session } = useStore();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customTags, setCustomTags] = useState([]);

  const isAuthenticated = !!session?.user;

  useEffect(() => {
    fetchChangelog();
    fetchCustomTags();
  }, []);

  const fetchChangelog = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use the database function to get published entries with sections
      const { data, error } = await supabase.rpc(
        "get_published_whats_new_with_sections"
      );

      if (error) throw error;

      setEntries(data || []);
    } catch (err) {
      console.error("Error fetching changelog:", err);
      setError("Failed to load changelog entries");
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomTags = async () => {
    try {
      const { data, error } = await supabase
        .from("whats_new_custom_tags")
        .select("*")
        .eq("is_active", true);

      if (error) throw error;
      setCustomTags(data || []);
    } catch (err) {
      console.error("Error fetching custom tags:", err);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const renderTag = (tag) => {
    const icon = tagIcons[tag] || faStar;
    const colorClass =
      tagColors[tag] || "bg-gray-500/20 text-gray-400 border-gray-500/30";

    return (
      <span
        key={tag}
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border ${colorClass}`}
      >
        <FontAwesomeIcon icon={icon} className="w-3 h-3" />
        {tag.replace("_", " ")}
      </span>
    );
  };

  const renderCustomTag = (tagName) => {
    const customTag = customTags.find((t) => t.tag_name === tagName);
    if (!customTag) return null;

    const iconMap = {
      star: faStar,
      tag: faStar, // Use faStar as fallback for now
      bell: faBell,
      wrench: faWrench,
      bug: faBug,
      bullhorn: faBullhorn,
    };

    const icon = iconMap[customTag.icon_name] || faStar;
    const colorClass =
      tagColors[customTag.color_class] ||
      "bg-gray-500/20 text-gray-400 border-gray-500/30";

    return (
      <span
        key={tagName}
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border ${colorClass}`}
      >
        <FontAwesomeIcon icon={icon} className="w-3 h-3" />
        {customTag.display_name}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-deeper">
        {/* Header */}
        <header className="border-b border-dark-elevated bg-dark-deeper">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              {/* Logo */}
              <Link href="/" className="flex items-center space-x-2">
                <Image
                  src="/logos/beta/white.svg"
                  alt="Qensa Logo"
                  width={100}
                  height={100}
                />
              </Link>

              {/* Navigation */}
              <nav className="hidden md:flex items-center space-x-8">
                <Link
                  href="/qonnect"
                  className="text-neutral-light/70 hover:text-neutral-light transition-colors"
                >
                  QONNECT
                </Link>
                <Link
                  href="/faq"
                  className="text-neutral-light/70 hover:text-neutral-light transition-colors"
                >
                  FAQ
                </Link>
                <Link
                  href="/changelog"
                  className="text-accent-lime font-medium"
                >
                  CHANGELOG
                </Link>
              </nav>

              {/* Right side */}
              <div className="flex items-center space-x-4">
                {isAuthenticated ? (
                  <Link href="/user/spaces">
                    <div className="w-12 h-12 rounded-full bg-neutral-light/20 hover:bg-neutral-light/30 flex items-center justify-center transition-colors">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="w-6 h-6 text-neutral-light"
                      >
                        <path d="M11.47 3.84a.75.75 0 011.06 0l8.69 8.69a.75.75 0 101.06-1.06l-8.689-8.69a2.25 2.25 0 00-3.182 0l-8.69 8.69a.75.75 0 001.061 1.06l8.69-8.69z" />
                        <path d="M12 5.432l8.159 8.159c.03.03.06.058.091.086v6.198c0 1.035-.84 1.875-1.875 1.875H15a.75.75 0 01-.75-.75v-4.5a.75.75 0 00-.75-.75h-3a.75.75 0 00-.75.75V21a.75.75 0 01-.75.75H5.625a1.875 1.875 0 01-1.875-1.875v-6.198a2.29 2.29 0 00.091-.086L12 5.43z" />
                      </svg>
                    </div>
                  </Link>
                ) : (
                  <Link
                    href="/onboarding"
                    className="border border-neutral-light text-neutral-light px-4 py-2 rounded-lg font-medium hover:bg-neutral-light hover:text-dark-deeper transition-colors"
                  >
                    Create your Qensa
                  </Link>
                )}
              </div>
            </div>
          </div>
        </header>

        <div className="container mx-auto px-4 py-12">
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center gap-3 text-neutral-light">
              <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
              <span>Loading changelog...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-dark-deeper">
        {/* Header */}
        <header className="border-b border-dark-elevated bg-dark-deeper">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              {/* Logo */}
              <Link href="/" className="flex items-center space-x-2">
                <Image
                  src="/logos/beta/white.svg"
                  alt="Qensa Logo"
                  width={100}
                  height={100}
                />
              </Link>

              {/* Navigation */}
              <nav className="hidden md:flex items-center space-x-8">
                <Link
                  href="/qonnect"
                  className="text-neutral-light/70 hover:text-neutral-light transition-colors"
                >
                  QONNECT
                </Link>
                <Link
                  href="/faq"
                  className="text-neutral-light/70 hover:text-neutral-light transition-colors"
                >
                  FAQ
                </Link>
                <Link
                  href="/changelog"
                  className="text-accent-lime font-medium"
                >
                  CHANGELOG
                </Link>
              </nav>

              {/* Right side */}
              <div className="flex items-center space-x-4">
                {isAuthenticated ? (
                  <Link href="/user/spaces">
                    <div className="w-12 h-12 rounded-full bg-neutral-light/20 hover:bg-neutral-light/30 flex items-center justify-center transition-colors">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        className="w-6 h-6 text-neutral-light"
                      >
                        <path d="M11.47 3.84a.75.75 0 011.06 0l8.69 8.69a.75.75 0 101.06-1.06l-8.689-8.69a2.25 2.25 0 00-3.182 0l-8.69 8.69a.75.75 0 001.061 1.06l8.69-8.69z" />
                        <path d="M12 5.432l8.159 8.159c.03.03.06.058.091.086v6.198c0 1.035-.84 1.875-1.875 1.875H15a.75.75 0 01-.75-.75v-4.5a.75.75 0 00-.75-.75h-3a.75.75 0 00-.75.75V21a.75.75 0 01-.75.75H5.625a1.875 1.875 0 01-1.875-1.875v-6.198a2.29 2.29 0 00.091-.086L12 5.43z" />
                      </svg>
                    </div>
                  </Link>
                ) : (
                  <Link
                    href="/onboarding"
                    className="border border-neutral-light text-neutral-light px-4 py-2 rounded-lg font-medium hover:bg-neutral-light hover:text-dark-deeper transition-colors"
                  >
                    Create your Qensa
                  </Link>
                )}
              </div>
            </div>
          </div>
        </header>

        <div className="container mx-auto px-4 py-12">
          <div className="text-center py-12">
            <div className="text-red-400 mb-4">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="text-4xl"
              />
            </div>
            <h2 className="text-xl font-semibold text-neutral-light mb-2">
              Error Loading Changelog
            </h2>
            <p className="text-neutral-light/60 mb-4">{error}</p>
            <button
              onClick={fetchChangelog}
              className="px-4 py-2 bg-accent-coral hover:bg-accent-coral/80 text-neutral-light rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-deeper">
      {/* Header */}
      <header className="border-b border-dark-elevated bg-dark-deeper">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link href="/" className="flex items-center space-x-2">
              <Image
                src="/logos/beta/white.svg"
                alt="Qensa Logo"
                width={100}
                height={100}
              />
            </Link>

            {/* Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <Link
                href="/qonnect"
                className="text-neutral-light/70 hover:text-neutral-light transition-colors"
              >
                QONNECT
              </Link>
              <Link
                href="/faq"
                className="text-neutral-light/70 hover:text-neutral-light transition-colors"
              >
                FAQ
              </Link>
              <Link href="/changelog" className="text-accent-lime font-medium">
                CHANGELOG
              </Link>
            </nav>

            {/* Right side */}
            <div className="flex items-center space-x-4">
              {isAuthenticated ? (
                <Link href="/user/spaces">
                  <div className="w-12 h-12 rounded-full bg-neutral-light/20 hover:bg-neutral-light/30 flex items-center justify-center transition-colors">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="w-6 h-6 text-neutral-light"
                    >
                      <path d="M11.47 3.84a.75.75 0 011.06 0l8.69 8.69a.75.75 0 101.06-1.06l-8.689-8.69a2.25 2.25 0 00-3.182 0l-8.69 8.69a.75.75 0 001.061 1.06l8.69-8.69z" />
                      <path d="M12 5.432l8.159 8.159c.03.03.06.058.091.086v6.198c0 1.035-.84 1.875-1.875 1.875H15a.75.75 0 01-.75-.75v-4.5a.75.75 0 00-.75-.75h-3a.75.75 0 00-.75.75V21a.75.75 0 01-.75.75H5.625a1.875 1.875 0 01-1.875-1.875v-6.198a2.29 2.29 0 00.091-.086L12 5.43z" />
                    </svg>
                  </div>
                </Link>
              ) : (
                <Link
                  href="/onboarding"
                  className="border border-neutral-light text-neutral-light px-4 py-2 rounded-lg font-medium hover:bg-neutral-light hover:text-dark-deeper transition-colors"
                >
                  Create your Qensa
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 bg-accent-coral/20 rounded-xl flex items-center justify-center">
              <FontAwesomeIcon
                icon={faBell}
                className="text-accent-coral text-xl"
              />
            </div>
            <h1 className="text-3xl font-bold text-neutral-light">Changelog</h1>
          </div>
          <p className="text-neutral-light/60 text-lg">
            Stay up to date with the latest features, improvements, and fixes
          </p>
        </div>

        {/* Entries */}
        {entries.length === 0 ? (
          <div className="text-center py-12">
            <FontAwesomeIcon
              icon={faBell}
              className="text-neutral-light/30 text-4xl mb-4"
            />
            <h3 className="text-lg font-medium text-neutral-light mb-2">
              No updates yet
            </h3>
            <p className="text-neutral-light/60">
              Check back soon for the latest updates and improvements!
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {entries.map((entry) => (
              <article
                key={entry.id}
                className="bg-dark-elevated rounded-xl border border-dark-surface overflow-hidden"
              >
                {/* Entry Header */}
                <div className="p-6 border-b border-dark-surface">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h2 className="text-xl font-semibold text-neutral-light">
                          {entry.title}
                        </h2>
                        {entry.version && (
                          <span className="text-sm text-neutral-light/60 bg-dark-surface px-2 py-1 rounded">
                            v{entry.version}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-3 text-sm text-neutral-light/60 mb-3">
                        <FontAwesomeIcon
                          icon={faCalendarAlt}
                          className="w-4 h-4"
                        />
                        <span>{formatDate(entry.published_at)}</span>
                      </div>

                      {((entry.tags && entry.tags.length > 0) ||
                        (entry.custom_tags &&
                          entry.custom_tags.length > 0)) && (
                        <div className="flex flex-wrap gap-2">
                          {entry.tags &&
                            entry.tags.map((tag) => renderTag(tag))}
                          {entry.custom_tags &&
                            entry.custom_tags.map((tag) =>
                              renderCustomTag(tag)
                            )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Summary or Description */}
                  <div className="text-neutral-light/80">
                    {entry.summary ? (
                      <p className="text-lg leading-relaxed">{entry.summary}</p>
                    ) : (
                      <p className="leading-relaxed">{entry.description}</p>
                    )}
                  </div>
                </div>

                {/* Entry Content */}
                <div className="p-6">
                  {entry.rich_content &&
                  entry.sections &&
                  entry.sections.length > 0 ? (
                    // Rich content sections
                    <div className="space-y-6">
                      {entry.sections.map((section) => (
                        <RichContentSection
                          key={section.id}
                          section={section}
                        />
                      ))}
                    </div>
                  ) : (
                    // Fallback to description if no rich content
                    !entry.summary && (
                      <div className="prose prose-invert max-w-none">
                        <div
                          className="text-neutral-light/80"
                          dangerouslySetInnerHTML={{
                            __html: entry.description,
                          }}
                        />
                      </div>
                    )
                  )}
                </div>
              </article>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-12 pt-8 border-t border-dark-surface">
          <p className="text-neutral-light/60">
            Want to go back?{" "}
            <Link
              href="/"
              className="text-accent-coral hover:text-accent-coral/80 transition-colors"
            >
              Return to Home
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
